import { connectStreamSource, disconnectStreamSource } from "@hotwired/turbo";
import Honeybadger from "../honeybadger";

// Our <turbo-cable-stream-source>: one Action Cable subscription per stream,
// shared by every element on the page that names it and released a tick
// after the last of them has left.
//
// turbo-rails' element subscribes in connectedCallback and unsubscribes in
// disconnectedCallback. Turbo Drive renders a cached preview of a page and
// then the fresh response, swapping the whole <body> in one go, so that
// element sends "unsubscribe" and "subscribe" for the same stream back to
// back. Action Cable runs the commands of one connection on a thread pool:
// the subscribe can be checked (and dropped as a duplicate) before the
// unsubscribe removes the old subscription. The browser then thinks it is
// subscribed while the server streams nothing, and every later broadcast
// (photo thumbnails, for one) is lost until the next navigation. Keeping the
// subscription alive across the swap means nothing is sent at all.
//
// Imported before @hotwired/turbo-rails, which only defines the element when
// nothing else has. The consumer is still turbo-rails' own.

const streams = new Map();

// turbo-rails' cable helper, handed over by application.js right after it
// imports turbo-rails. Importing turbo-rails here (even dynamically) would make
// esbuild's code splitting hoist it into a shared chunk that evaluates, and
// defines its own element, before this module.
let provideCable;
const turboCable = new Promise((resolve) => (provideCable = resolve));

export function useCable(cable) {
  provideCable(cable);
}

class TurboCableStreamSourceElement extends HTMLElement {
  static observedAttributes = ["channel", "signed-stream-name"];

  connectedCallback() {
    connectStreamSource(this);
    this.stream = acquire(this);
  }

  disconnectedCallback() {
    disconnectStreamSource(this);
    if (this.stream) release(this, this.stream);
    this.stream = null;
    this.subscriptionDisconnected();
  }

  attributeChangedCallback() {
    if (this.stream) {
      this.disconnectedCallback();
      this.connectedCallback();
    }
  }

  dispatchMessageEvent(data) {
    return this.dispatchEvent(new MessageEvent("message", { data }));
  }

  subscriptionConnected() {
    this.setAttribute("connected", "");
  }

  subscriptionDisconnected() {
    this.removeAttribute("connected");
  }

  get channel() {
    const channel = this.getAttribute("channel");
    const signed_stream_name = this.getAttribute("signed-stream-name");
    return { channel, signed_stream_name, ...snakeize({ ...this.dataset }) };
  }
}

function acquire(element) {
  const channel = element.channel;
  const identifier = JSON.stringify(channel);
  let stream = streams.get(identifier);
  if (!stream) {
    stream = { identifier, channel, elements: new Set(), connected: false };
    stream.subscription = subscribe(stream);
    streams.set(identifier, stream);
  }
  clearTimeout(stream.releaseTimer);
  stream.elements.add(element);
  if (stream.connected) element.subscriptionConnected();
  return stream;
}

// Deferred by a macrotask so that a body swap (old element out, new element
// in, within one DOM operation) never touches the server.
function release(element, stream) {
  stream.elements.delete(element);
  clearTimeout(stream.releaseTimer);
  stream.releaseTimer = setTimeout(() => {
    if (stream.elements.size > 0) return;
    streams.delete(stream.identifier);
    stream.subscription.then((subscription) => subscription?.unsubscribe());
  }, 0);
}

// Action Cable opens the WebSocket synchronously inside subscriptions.create,
// so a browser that refuses to construct it (seen in production: "Failed to
// create a WebSocket: the provided URL is invalid" on the default "/cable"
// mount path) rejects this promise. Nobody awaits it, so it surfaced as an
// unhandled rejection. The page works without live updates; report the
// failure once with what the browser resolved. That report showed a valid
// wss:// URL refused by an old engine behind a modern user agent, so that
// wording is dropped as noise (honeybadger/ignorable_notice.js).
async function subscribe(stream) {
  const cable = await turboCable;
  try {
    return await cable.subscribeTo(stream.channel, {
      received: (data) => stream.elements.forEach((element) => element.dispatchMessageEvent(data)),
      connected: () => {
        stream.connected = true;
        stream.elements.forEach((element) => element.subscriptionConnected());
      },
      disconnected: () => {
        stream.connected = false;
        stream.elements.forEach((element) => element.subscriptionDisconnected());
      }
    });
  } catch (error) {
    Honeybadger.notify(error, { context: await cableDiagnostics(cable) });
    return null;
  }
}

async function cableDiagnostics(cable) {
  const consumer = await cable.getConsumer?.();
  let url;
  try {
    url = consumer?.url;
  } catch (error) {
    url = `unresolvable: ${error.message}`;
  }
  return { cable_url: String(url), base_uri: document.baseURI, location_protocol: window.location.protocol };
}

// data-foo-bar → foo_bar, as turbo-rails sends extra stream params.
function snakeize(object) {
  return Object.fromEntries(
    Object.entries(object).map(([key, value]) => [
      key[0].toLowerCase() + key.slice(1).replace(/([A-Z]+)/g, (_, upper) => `_${upper.toLowerCase()}`),
      value
    ])
  );
}

customElements.define("turbo-cable-stream-source", TurboCableStreamSourceElement);
